/**
 * Helper that performs an AI Edit on the current cover image using Gemini.
 * Returns the new public URL.
 */
import { geminiEdit, generateRequestId } from "./api";

// Card canvas is 600x400 (3:2 landscape)
const DEFAULT_CARD_SIZE = "1536x1024";

/**
 * Apply a Gemini AI edit to an image with optional progress tracking.
 * 
 * @param {string} imageUrl - URL of the image to edit
 * @param {File|null} maskFile - Optional mask file (used for prompt context, Gemini uses semantic masking)
 * @param {string} prompt - The edit prompt
 * @param {string} model - Optional model override (ignored for Gemini)
 * @param {string} size - Image size (default: 1536x1024)
 * @param {function|null} onProgress - Optional callback for progress updates ({ stage, percent })
 * @returns {Promise<string>} - The new image URL
 */
export async function applyDalleEdit(imageUrl, maskFile, prompt, model = "", size = DEFAULT_CARD_SIZE, onProgress = null) {
  console.log("[applyGeminiEdit] →", { imageUrl, maskFile, prompt, size });
  
  // Generate a unique request ID for tracking
  const requestId = generateRequestId();
  
  try {
    // Pull the current cover as a Blob
    if (onProgress) {
      onProgress({ stage: "Loading image...", percent: 5 });
    }
    
    const resp = await fetch(imageUrl);
    if (!resp.ok) {
      throw new Error(`Failed to fetch image: ${resp.status} ${resp.statusText}`);
    }
    
    const imgBlob = await resp.blob();
    if (!imgBlob || imgBlob.size === 0) {
      throw new Error("Invalid image data received");
    }

    console.log("[applyGeminiEdit] Fetched base image", {
      size: imgBlob.size,
      type: imgBlob.type,
    });

    // Build multipart form-data
    const fd = new FormData();
    fd.append("image", new File([imgBlob], "base.png", { type: imgBlob.type }));
    if (maskFile) fd.append("mask", maskFile);
    fd.append("prompt", prompt);
    fd.append("size", size);
    fd.append("request_id", requestId);

    if (maskFile) {
      console.log("[applyGeminiEdit] Mask provided (for context)", {
        size: maskFile.size,
        type: maskFile.type,
      });
    }

    if (onProgress) {
      onProgress({ stage: "Sending to Gemini AI...", percent: 15 });
    }

    console.log("[applyGeminiEdit] Sending form-data to /gemini_edit/");

    // Simulate progress updates since Gemini doesn't have SSE progress
    let progressInterval = null;
    let currentPercent = 15;
    
    if (onProgress) {
      progressInterval = setInterval(() => {
        if (currentPercent < 85) {
          currentPercent += Math.random() * 10;
          onProgress({ 
            stage: currentPercent < 40 ? "Processing with Gemini AI..." : 
                   currentPercent < 70 ? "Generating edited image..." : 
                   "Finalizing...", 
            percent: Math.min(currentPercent, 85) 
          });
        }
      }, 2000);
    }

    try {
      // Hit backend wrapper
      const { data } = await geminiEdit(fd);

      console.log("[applyGeminiEdit] ← Response", data);
      
      if (!data || !data.public_url) {
        throw new Error("Invalid response from server - missing public_url");
      }
      
      if (onProgress) {
        onProgress({ stage: "Complete!", percent: 100 });
      }
      
      return data.public_url;
    } finally {
      if (progressInterval) {
        clearInterval(progressInterval);
      }
    }
  } catch (error) {
    console.error("[applyGeminiEdit] Error:", error);
    if (onProgress) {
      onProgress({ stage: "Error occurred", percent: -1, error: error.message });
    }
    throw error;
  }
}
